import React, { useEffect, useMemo, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { StateContext } from "../stateContext";
import ViewNumericalUpload from "./NumericalUploadPage";
import CaptionsModeViewer from "../components/CaptionsModeViewer";
import { formatDateTime } from "../utils/timeUtils";
import { useUser } from '../utils/UserProvider';
import local_storage from "../utils/local_storage";
import { getResultById } from "../utils/localDatabase";
import ViewScrollingGroupsUpload from "../components/ScrollMode";
import MultiBoxCaptionsModeViewer from "../components/MultiBoxCaptionsModeViewer";
import ScrollingListDetector from "../components/scrolling-list-detector";

// const MODES = ["presentation", "numerical", "captions", "scroll"];

export default function ViewUploadPage() {
  const { user } = useUser();
  let { uploadID } = useParams();
  const navigate = useNavigate();
  const { setToastData } = useContext(StateContext);
  const location = useLocation();
  const path = location.pathname;
  const [mode, setMode] = useState("presentation");
  const [loading, setLoading] = useState(true);
  const [isSlow, setIsSlow] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(true);
  const [uploadData, setUploadData] = useState(null);
  const [results, setResults] = useState(null); // presentation: {text}; numerical: {ocrResults, maxValues}
  const [urls, setUrls] = useState([]); // signed frame URLs
  const [linesByFrame, setLinesByFrame] = useState([]); 
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [error, setError] = useState(null);
  // Similarity filter (percent, 0–100)
const [simThreshold, setSimThreshold] = useState(95);
// Per-frame flag: is frame i similar to frame i-1?
const [similarToPrev, setSimilarToPrev] = useState([]);

  // NEW: which frames are selected for export (presentation mode)
  const [selectedFrames, setSelectedFrames] = useState([]);
// Function to convert results + blobs into uploadData
async function convertResultsToUploadData(resultData) {
    // Convert blobs to base64 data URLs
    // const urls = await Promise.all(
    //     results.blobs.map(blob => new Promise(resolve => {
    //         const reader = new FileReader();
    //         reader.onload = () => resolve(reader.result);
    //         reader.readAsDataURL(blob); // Converts blob to base64 URL
    //     }))
    // );

    const nowSeconds = Math.floor(Date.now() / 1000);
    // const resultData = await getResultById(uploadID)

    const uploadDataObj = {
        uploadData: {
            userID: "PLACEHOLDER_USER_ID",
            ownerUid: "PLACEHOLDER_OWNER_UID",
            basePath: "uploads/" + crypto.randomUUID(),
            createdAt: {_seconds: resultData.uploadedAt},
            targetFrameRate: 15,
            sortPreference: null,
            mimeType: "image/jpeg",
            isVideo: true,
            uploadName: resultData.fileName || "Local Upload",
            mediaContent: "numerical",
            originalLanguage: null,
            targetLanguage: null,
            maxFrames: urls.length,
            ocrType: "local",
            creditCost: 1,
            source: "direct",
            uploadedAt:  {_seconds: resultData.uploadedAt},
            processedAt: {_seconds: resultData.uploadedAt},
            status: "processed"
        },
        results: convertFramesToOcrResults(resultData.data),
        urls: resultData.data.map(result => result.thumb), // base64 versions of blobs
        manifest: {
            path: "raw_ocr/" + crypto.randomUUID() + "/manifest.json",
            filesCount: urls.length
        },
    };
    uploadDataObj.results.linesByFrame = resultData.data.map(result => result.text.split("\n"))
    // uploadDataObj.maxValues = uploadDataObj.ocrResults.maxValues
    return uploadDataObj;
}

function convertFramesToOcrResults(frames) {
  function nboxToVertices(nbox, imgW, imgH) {
    const { x, y, w, h } = nbox;

    // Scale normalized coords to image resolution
    const absX = x * imgW;
    const absY = y * imgH;
    const absW = w * imgW;
    const absH = h * imgH;

    return [
      { x: Math.round(absX),       y: Math.round(absY) },        // top-left
      { x: Math.round(absX+absW),  y: Math.round(absY) },        // top-right
      { x: Math.round(absX+absW),  y: Math.round(absY+absH) },   // bottom-right
      { x: Math.round(absX),       y: Math.round(absY+absH) }    // bottom-left
    ];
  }

  let maxDistance = 0;
  let maxSizeDifference = 0;
  let maxTextLength = 0;

  const ocrResults = frames.map((frame, fileIndex) => {
    const { ocrW, ocrH } = frame;

    const detections = frame.words.map((word) => {
      const absW = word.nbox.w * ocrW;
      const absH = word.nbox.h * ocrH;
      const area = Math.round(absW * absH);

      // Track metrics
      const sizeDifference = Math.abs(absW - absH);
      if (sizeDifference > maxSizeDifference) maxSizeDifference = sizeDifference;
      if (word.text.length > maxTextLength) maxTextLength = word.text.length;

      // Distance = diagonal length of box
      const distance = Math.sqrt(absW * absW + absH * absH);
      if (distance > maxDistance) maxDistance = distance;

      return {
        text: word.text,
        confidence: word.conf || null,
        blockType: "TEXT",
        vertices: nboxToVertices(word.nbox, ocrW, ocrH),
        area,
      };
    });

    return {
      t: frame.t,
      fileIndex,
      text: frame.text,
      detections,
    };
  });

  return {
    maxValues: {
      maxDistance: Math.round(maxDistance),
      maxSizeDifference: Math.round(maxSizeDifference),
      maxTextLength,
    },
    ocrResults,
    linesByFrame: frames.map(result => result.text.split("\n")),
    processingType: "numerical",
  };
}




async function fetchDetails() {
  try {
    setLoading(true);
    setError(null);
    setIsSlow(false); // reset the "slow" flag

    // Start a timer to mark the request as slow if it exceeds 5 seconds
    const slowTimer = setTimeout(() => {
      setIsSlow(true);
    }, 5000);

    let data;

    if (path.includes('/local')) {
      const localDBDoc = await getResultById(uploadID);
      const uploadDatObj = await convertResultsToUploadData(localDBDoc);
      setUploadData(uploadDatObj.uploadData);
      setResults(uploadDatObj.results);
      setUrls(uploadDatObj.urls);
      setLinesByFrame(uploadDatObj.results.linesByFrame);
    } else {
      if (path.includes('/demo')) uploadID = "demo";
      const response = await apiClient.get(
        `/getUploadDetails/${uploadID}?mode=numerical`, { timeout: 180000 }
      );
      data = response.data;
      setUploadData(data.uploadData || null);
      setResults(data.results || null);
      setUrls(data.urls || []);
      setLinesByFrame(data.results.linesByFrame || []);
    }

    setSelectedIndex(0);
    applySimilarityFilter();

    // Request finished, cancel slow timer
    clearTimeout(slowTimer);
    setIsSlow(false);
  } catch (e) {
    console.log(e.message);
    setError("Unable to load upload details yet.");
    setToastData?.({ text: "Results not yet available.", show: true });
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    if (user || path.includes('/demo')) fetchDetails();
    // else console.log("not fetching")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, path]);

  // NEW: initialize/resize selectedFrames when frames change
  const presentationFramesCount = useMemo(() => {
    const countFromResults = linesByFrame?.length ?? 0;
    const countFromLocal = linesByFrame?.length ?? 0;
    const countFromUrls = urls?.length ?? 0;
    return countFromResults || countFromLocal || countFromUrls || 0;
  }, [results, linesByFrame, urls]);

  useEffect(() => {
    if (presentationFramesCount > 0) {
      setSelectedFrames((prev) => {
        // keep previous selections where possible; default checked
        const next = Array(presentationFramesCount).fill(true);
        for (let i = 0; i < Math.min(prev.length, next.length); i++) {
          next[i] = prev[i] ?? true;
        }
        return next;
      });
    } else {
      setSelectedFrames([]);
    }
  }, [presentationFramesCount]);

    const combinedFrameTexts = useMemo(() => {
    const arr = linesByFrame || linesByFrame || [];
    return arr.map((lines) =>
        Array.isArray(lines)
        ? lines.join("\n").toLowerCase().replace(/\s+/g, " ").trim()
        : ""
    );
    }, [results, linesByFrame]);

    useEffect(() => {
    const n = combinedFrameTexts.length;
    if (n) setSimilarToPrev(Array(n).fill(false));
    else setSimilarToPrev([]);
    }, [combinedFrameTexts.length, linesByFrame]);

    function tokenize(s) {
    // split on non-letters/digits, drop empties
    return s.split(/[^a-z0-9]+/i).filter(Boolean);
    }

    function jaccardSimilarity(a, b) {
    // Handle empties
    if (!a && !b) return 1;
    if (!a || !b) return 0;

    const A = new Set(tokenize(a));
    const B = new Set(tokenize(b));
    if (A.size === 0 && B.size === 0) return 1;

    let inter = 0;
    for (const t of A) if (B.has(t)) inter++;
    const uni = A.size + B.size - inter;
    return uni === 0 ? 1 : inter / uni; // 0..1
    }

    function applySimilarityFilter() {
    const n = combinedFrameTexts.length;
    if (n <= 0) return;

    const flags = Array(n).fill(false); // flags[i] = true if frame i is similar to i-1
    for (let i = 1; i < n; i++) {
        const simPct = Math.round(
        jaccardSimilarity(combinedFrameTexts[i - 1], combinedFrameTexts[i]) * 100
        );
        flags[i] = simPct >= simThreshold; // similarity threshold (not difference)
    }
    setSimilarToPrev(flags);

    // Re-check non-similar frames; uncheck similar frames
    setSelectedFrames(() => {
        const next = Array(n).fill(true);
        // keep frame 0 checked (no previous to compare)
        next[0] = true;
        for (let i = 1; i < n; i++) {
        next[i] = !flags[i]; // if similar → false (unchecked); else true (rechecked)
        }
        return next;
    });
    }


  const frameText = useMemo(() => {
    if (!linesByFrame) return "";
    const frame = linesByFrame[selectedIndex];
    if (!frame) return "";
    return frame.join("\n");
  }, [results, mode, selectedIndex, linesByFrame]);

  // NEW: toggle selection
  const toggleFrameSelected = (i) => {
    setSelectedFrames((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  };

  const exportSelectedSlides = () => {
    const arr = linesByFrame || [];
    if (!arr.length) return;

    const blocks = [];
    for (let i = 0; i < arr.length; i++) {
      if (!selectedFrames[i]) continue;
      const lines = Array.isArray(arr[i]) ? arr[i] : [];
      const text = lines.join("\n");
      blocks.push(`Frame ${i + 1}\n-------------------\n${text}\n`);
    }
    if (!blocks.length) return;

    const payload = "\uFEFF" + blocks.join("\n");
    const blob = new Blob([payload], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${uploadID}-selected-slides.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const anySelected = selectedFrames.some(Boolean);

  return (
    <div className="w-full max-w-7xl mx-auto px-4">
      <div className="flex items-center justify-between mb-4">
        <button className="btn btn-ghost btn-xs sm:btn-sm md:btn-md" onClick={() => navigate('/uploads')}>
          ← Uploads
        </button>
        {/* Mode hint */}
        {/* <div className="mt-2 text-xs opacity-60 hidden md:block">
          {mode === "presentation" && (
            <div className="mt-1">
              Presentation view shows merged slide text for each frame. Switch to 'numerical' to select specific text blocks per frame.
            </div>
          )}
          {mode === "numerical" && (
            <div className="mt-1">
              Numerical view shows text blocks for each frame. Switch to 'presentation' for merged slide text.
            </div>
          )}
          {mode === "captions" && (
            <div className="mt-1">
              Captions view shows text blocks with their timestamps. Switch to 'presentation' for merged slide text.
            </div>
          )}
        </div> */}
        <div role="tablist" className="tabs tabs-bordered tabs-sm md:tabs-md">
            <a role="tab" className={`tab${mode === "presentation" ? " tab-active" : ""}`} onClick={() => setMode("presentation")}>Presentation</a>
            <a role="tab" className={`tab${mode === "numerical" ? " tab-active" : ""}`} onClick={() => setMode("numerical")}>Numerical</a>
            <a role="tab" className={`tab${mode === "captions" ? " tab-active" : ""}`} onClick={() => setMode("captions")}>Region</a>
            <a role="tab" className={`tab${mode === "scroll" ? " tab-active" : ""}`} onClick={() => setMode("scroll")}>Scroll</a>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-xs sm:btn-sm md:btn-md" onClick={() => setInfoModalOpen(true)}>?</button>
          {/* <label className="label-text mr-1">View:</label>
          <select
            className="select select-bordered select"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            {MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select> */}
        </div>
      </div>

      {/* Upload summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="card bg-base-200 shadow">
          <div className="card-body p-4">
            <p className="text-lg"><strong>Upload:</strong> {uploadData?.uploadName || "(untitled)"}</p>
            <div className="text-md">
              <div className="opacity-70">
                Uploaded at: {uploadData?.createdAt?._seconds
                  ? formatDateTime(uploadData.createdAt, { dateStyle: 'medium', timeStyle: 'short' })
                  : uploadData?.createdAt?._seconds || ""}
              </div>
            </div>
            <div className="text-md">
              <div className="opacity-70">
                Processing Type: {uploadData?.ocrType}
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-base-200 shadow">
          <div className="card-body p-4">
            <h3 className="card-title text-md">Details</h3>
            <div className="text-md">
              <div>Format: {uploadData?.isVideo ? "Video" : "Pictures"}</div>
            </div>
            <div className="text-md">
              <div>Frames: {urls?.length || 0}</div>
              {/* {(mode === "numerical" || mode === "captions") && results?.maxValues && (
                <div className="opacity-70 mt-1">
                  maxLen: {results.maxValues.maxTextLength ?? 0} •
                  maxDist: {results.maxValues.maxDistance ?? 0} •
                  maxSizeΔ: {results.maxValues.maxSizeDifference ?? 0}
                </div>
              )} */}
            </div>
          </div>
        </div>

        {false && <div className="card bg-base-200 shadow">
          <div className="card-body p-4">
            <h3 className="card-title text-sm">Processing Type</h3>
            <div className="flex flex-wrap gap-2">
              {/* <button
                className="btn btn-sm"
                onClick={() => {
                  const blob = new Blob([frameText || ""], { type: "text/plain;charset=utf-8" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `${uploadID}-${mode}.txt`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
                disabled={!frameText}
              >
                Download Frame Text
              </button> */}

              {/* NEW: Download only selected slides (presentation) */}
             {mode === "presentation" && <button
                className="btn btn-sm btn-success"
                onClick={exportSelectedSlides}
                disabled={mode !== "presentation" || !anySelected}
                title={mode !== "presentation" ? "Switch to presentation mode to export selected slides" : ""}
              >
                Download Selected Frames' Text
              </button>}
              {mode === "presentation" && (
                <span className="text-xs opacity-70 self-center">
                  {selectedFrames.filter(Boolean).length}/{presentationFramesCount} selected
                </span>
              )}
            </div>
          </div>
        </div>}
      </div>

      {/* Presentation Content */}
      {mode === "presentation" && (
        <div className=" md:h-[70vh">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            
            {/* Left: frames list with checkboxes */}
            <div className="lg:col-span-1 h-[50vh] md:h-[70vh] flex flex-col ">
                 {/* Similarity filter */}

                <div className="w-full mt-2 px-4 ">
                <label className="label cursor-default ">
                    <span className="label-text text-center w-full">
                    Filter frames that are  <b>{simThreshold}%</b> similar to previous
                    </span>
                </label>
                <input
                    type="range"
                    className="range range-xs md:range-sm"
                    min={80}
                    max={100}
                    step={1}
                    value={simThreshold}
                    onChange={(e) => setSimThreshold(Number(e.target.value))}
                    onMouseUp={applySimilarityFilter}
                    onTouchEnd={applySimilarityFilter}
                    onBlur={applySimilarityFilter}
                    disabled={mode !== "presentation" || combinedFrameTexts.length <= 1}
                    title={mode !== "presentation" ? "Switch to Presentation to use this filter" : ""}
                />
                </div>
                <span className="text-xs opacity-70 self-center">
                  {selectedFrames.filter(Boolean).length}/{presentationFramesCount} selected
                </span>
              <button
                className="btn btn-sm btn-success w-auto m-2"
                onClick={exportSelectedSlides}
                disabled={mode !== "presentation" || !anySelected}
                title={mode !== "presentation" ? "Switch to presentation mode to export selected slides" : ""}
              >
                Download Selected Frames Text
              </button>
              <div className="flex-1 min-h-0 rounded-lg border overflow-scroll">
                {loading ? (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="skeleton h-28 w-full" />
                    ))}
                  </div>
                ) : error ? (
                  <div className="p-4 text-error">{error}</div>
                ) : urls && urls.length ? (
                    <div className=" ">
                  <ul className="menu ">
                    {urls.map((u, i) => (
                      <li key={i} className="p-2">
                        <div className={`w-full flex items-center gap-3 ${i === selectedIndex ? "bg-base-200 rounded" : ""}`}>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            checked={!!selectedFrames[i]}
                            onChange={() => toggleFrameSelected(i)}
                            title="Include this frame in export"
                          />
                          <button
                            className="flex-1 text-left"
                            onClick={() => setSelectedIndex(i)}
                            title={`Frame ${i + 1}`}
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={u}
                                alt={`frame ${i + 1}`}
                                className="w-36 md:w-64 object-cover rounded border"
                                loading="lazy"
                              />
                              <div className="flex flex-col">
                                <div className="text-xs opacity-80">Frame {i + 1}</div>
                                    {similarToPrev?.[i] && (
                                        <span className="bg-red-500 text-white text-xs rounded p-1 w-auto">Similar to previous</span>
                                        )}
                                </div>
                            </div>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  </div>
                ) : (
                  <div className="p-4 opacity-70">No frames available.</div>
                )}
              </div>
             
            </div>

            {/* Right: extracted text for selected frame */}
            <div className="lg:col-span-1">
              <div className="lg:h-[70vh] overflow-scroll rounded-lg border p-4 whitespace-pre-wrap">
                {loading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="skeleton h-4 w-full" />
                    ))}
                  </div>
                ) : frameText ? (
                  <>
                    <div className="badge badge-outline mb-2">Frame {selectedIndex + 1}</div>
                    <div className="prose max-w-none text-sm">{frameText}</div> 
                  </>
                ) : (
                  <div className="opacity-70">No extracted text for this view.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === "numerical" && (
        <div>
          <ViewNumericalUpload results={results} urls={urls} />
        </div>
      )}

      {mode === "captions" && (
        <div>
          <MultiBoxCaptionsModeViewer results={results} urls={urls} />
        </div>
      )}
      {mode === "scroll" && (
        <div>
          <ScrollingListDetector results={results} urls={urls} />
        </div>
      )}
      {infoModalOpen && (
        <div className="modal modal-open">
            <div className="modal-box max-w-3xl">
            <h3 className="font-bold text-lg text-center">Which view should I use?</h3>
            <p className="py-2 text-center opacity-80">
                Each view uses the same underlying OCR results, just organized differently for your task.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Presentation */}
                <div className="card bg-base-200">
                <div className="card-body p-4">
                    <div className="badge badge-primary mb-2">Presentation</div>
                    <ul className="list-disc ml-4 space-y-1 text-sm">
                    <li><b>What you see:</b> Per-frame slide text as readable lines/paragraphs.</li>
                    <li><b>Best for:</b> Decks/slides or screenshots where you want clean text.</li>
                    <li><b>Why choose it:</b> Quick review, easy “Download Selected Text” per frame.</li>
                    <li><b>Note:</b> Geometry isn’t shown here (no box positions).</li>
                    </ul>
                </div>
                </div>

                {/* Numerical */}
                <div className="card bg-base-200">
                <div className="card-body p-4">
                    <div className="badge badge-secondary mb-2">Numerical</div>
                    <ul className="list-disc ml-4 space-y-1 text-sm">
                    <li><b>What you see:</b> Individual OCR text blocks with their positions.</li>
                    <li><b>Best for:</b> Tables, KPIs, changing numbers you want to track per frame.</li>
                    <li><b>Why choose it:</b> Select blocks, track across frames, export CSV.</li>
                    <li><b>Note:</b> Preserves block geometry/IDs for downstream analysis.</li>
                    </ul>
                </div>
                </div>

                {/* Captions */}
                <div className="card bg-base-200">
                <div className="card-body p-4">
                    <div className="badge badge-accent mb-2">Region</div>
                    <ul className="list-disc ml-4 space-y-1 text-sm">
                    <li><b>What you see:</b> A draggable/resizable box filters text to the defined area.</li>
                    <li><b>Best for:</b> Subtitles or text that appears in a specific region.</li>
                    <li><b>Why choose it:</b> Focus on that area only; export TXT or CSV with timestamps.</li>
                    <li><b>Note:</b> Only blocks fully inside the box are treated as captions.</li>
                    </ul>
                </div>
                </div>
            </div>

            {/* <div className="mt-4 p-3 rounded-lg bg-base-200">
                <p className="text-sm">
                <b>Tips:</b> You can switch views anytime. Presentation is fastest for reading; Numerical is
                best for structured exports; Captions isolates a region (e.g., subtitles) and supports
                length/area filters to clean up the output.
                </p>
            </div> */}

            {isSlow && (
                <div className="mt-4 p-3 rounded-lg bg-yellow-200 border border-yellow-400 text-yellow-800">
                <p className="text-sm">
                    <strong>Loading is taking longer than usual...</strong> This can happen with large uploads. If your upload does not load in a few minutes, contact support at help@textractify.com.
                </p>
                </div>
            )}

            <div className="modal-action">
                <button className={`btn ${loading ? "btn-disabled" : ""}`} onClick={() => setInfoModalOpen(false)}>{loading ? <div className="loading loading-spinner loading-sm" /> : "Got it!"}</button>
            </div>
            </div>
        </div>
        )}

    </div>
  );
}
